import { useCallback, useEffect, useRef, useState } from 'react';
import { STRINGS } from '@/strings/strings';
import { FotoUploadSlot } from './FotoUploadSlot';
import type { FotoUploadSlotState } from './FotoUploadSlot';
import {
  CAST_MAX_ENTRIES,
  CAST_PERSONEN_NOTIZ_MAX_LENGTH,
  CAST_SPECIES_MAX_LENGTH,
} from '../../state/book-configurator-store';
import type {
  CastGeschlecht,
  CastTextEntry,
  CastPersonType,
} from '../../state/book-configurator-store';
import { CharacterCounter } from '../geschichte/CharacterCounter';
import { CREATE_BOOK_ORDER_CAST_NAME_MAX_LENGTH } from '../../constants/text-limits';

/**
 * Sebastian ruling 2026-09-24 — one-question-at-a-time Personen flow.
 *
 * Owns the per-entry step machine for Block 1 (Personen). One question
 * visible at a time; previously answered questions collapse to compact
 * "Antwort · Ändern" rows above the currently-active question. Complete
 * entries collapse to a card with "✓ Name, N Jahre · Ändern · Entfernen"
 * (plus optional "Weiteres Foto hinzufügen" link on non-first entries).
 *
 * Parent (PersonenRoute) still owns:
 *   - the K2ConfirmDialog (fires on face-photo pick per 2026-09-18)
 *   - the analytics dedup (via sessionStorage) — flow signals via
 *     `onFirstPersonComplete` / `onFirstPhotoStepReached`
 *   - IDB hydration + HEIC preview machinery
 *
 * Round-2 additions (Sebastian 2026-09-24):
 *   - `relation` is a first-class flow step (own question). It appears
 *     AFTER the photo (post-K2 confirm) for every cast entry at
 *     index > 0 (first entry has no one to relate to). Weiter +
 *     Überspringen. Answering or skipping transitions to `complete`.
 *     Placeholder is role-specific (Kind / Erwachsener / Tier variants
 *     in strings.ts).
 *   - Every text question renders a Weiter button + advances on Enter.
 *     Blur-advance is gone — an invisible advance was disorienting.
 *   - Ändern on a collapsed card enters `edit_review` mode: the entry
 *     stays collapsed (all answered steps shown as compact rows) and
 *     a Fertig button closes the edit. Editing a single field jumps
 *     to that step then returns to `edit_review` on advance — so you
 *     can tweak name without walking through age/gender/photo again.
 *     Doch nicht is hidden in edit mode; only Entfernen can delete
 *     an existing person.
 *   - PhotoQuestion no longer duplicates the "Bis 10 MB…" line —
 *     FotoUploadSlot owns it, PhotoQuestion passes showLimitsLine=false.
 */

// ─── Types ─────────────────────────────────────────────────────────

/**
 * One step in the per-entry flow.
 *
 *   `role`          — Q1 role picker (kind / erwachsener / tier).
 *   `species`       — Tier-only, Q2. Text input + Weiter button.
 *   `name`          — Text input + Weiter button.
 *   `age`           — Number input + Weiter button.
 *   `gender`        — Three tap-to-advance buttons.
 *   `photo`         — FotoUploadSlot; auto-advance on K2 close.
 *                     For non-first entries, K2 close transitions to
 *                     `relation` (not `complete`) so the relationship
 *                     is asked AFTER the photo (Sebastian revised
 *                     ruling 2026-09-24 — photo is the core, relation
 *                     is a lighter add-on that closes the entry).
 *   `relation`      — Sebastian ruling 2026-09-24 (round 2 rev). Own
 *                     step, shown for EVERY cast entry at index > 0
 *                     AFTER the photo. Text input + Weiter ·
 *                     Überspringen buttons. Answering or skipping
 *                     transitions to `complete`.
 *   `complete`      — Collapsed card visible; person is done.
 *   `edit_review`   — Sebastian ruling 2026-09-24 (round 2). Entered
 *                     via Ändern on a collapsed card. Shows every
 *                     answered step as a compact row (each with its
 *                     own Ändern) plus a Fertig button that returns
 *                     to `complete`. Fertig is disabled while any
 *                     required field is missing (e.g. the customer
 *                     removed the photo mid-edit). Doch nicht
 *                     NEVER shows during edit — the only way to
 *                     delete an existing person is Entfernen.
 */
export type FlowStep =
  | 'role'
  | 'species'
  | 'name'
  | 'age'
  | 'gender'
  | 'relation'
  | 'photo'
  | 'complete'
  | 'edit_review';

export type FlowRole = 'kind' | 'erwachsener' | 'tier';

interface PhotoEntry {
  readonly file: File;
  readonly url: string | null;
  readonly status: 'ready' | 'converting' | 'heic';
}

interface CastPhotos {
  readonly face: PhotoEntry | null;
}

type PhotoMap = ReadonlyMap<string, CastPhotos>;

export interface PersonenFlowProps {
  readonly cast: readonly CastTextEntry[];
  readonly addCast: () => string;
  readonly updateCast: (
    key: string,
    patch: Partial<
      Pick<
        CastTextEntry,
        'name' | 'ageYears' | 'personType' | 'geschlecht' | 'personenNotiz' | 'species'
      >
    >,
  ) => void;
  readonly removeCast: (key: string) => void;
  readonly photos: PhotoMap;
  readonly setFacePhoto: (castKey: string, file: File | null) => void;
  /** Parent uses this to open K2 for the entry the customer just picked a photo for. */
  readonly onFacePickedForK2: (castKey: string) => void;
  /** Parent tells the flow which entry's K2 is currently open (null = closed). */
  readonly k2OpenKey: string | null;
  /** Parent handles Block 1 → Block 2 transition (markAnswered(1)). */
  readonly onAllCastComplete: () => void;
  /** Fires the first time any entry transitions to `complete` in this session. */
  readonly onFirstPersonComplete: () => void;
  /** Fires the first time any entry reaches the photo step in this session. */
  readonly onFirstPhotoStepReached: () => void;
  /**
   * Analytics callbacks (Sebastian ruling 2026-10-08). Fire from the
   * flow when the customer commits a per-entry step; the parent owns
   * per-entry dedup + the phCapture emission.
   */
  readonly onRecipientChosen: (castKey: string, role: FlowRole, castIndex: number) => void;
  readonly onPersonNameEntered: (castKey: string, castIndex: number) => void;
  readonly onPersonCompleted: (castKey: string, castIndex: number, castSize: number) => void;
}

// ─── Step-order helper ─────────────────────────────────────────────

// Base step orders per role. `relation` is appended conditionally by
// `stepsForRole` for non-first entries only (castIndex > 0), AFTER
// the photo (Sebastian ruling 2026-09-24 revised — photo is the core
// of each person; relationship is a lighter add-on that closes the
// entry).
const STEP_ORDER_PERSON_FIRST: readonly FlowStep[] = ['role', 'name', 'age', 'gender', 'photo'];
const STEP_ORDER_PERSON_NON_FIRST: readonly FlowStep[] = [
  'role',
  'name',
  'age',
  'gender',
  'photo',
  'relation',
];
const STEP_ORDER_TIER_FIRST: readonly FlowStep[] = [
  'role',
  'species',
  'name',
  'age',
  'gender',
  'photo',
];
const STEP_ORDER_TIER_NON_FIRST: readonly FlowStep[] = [
  'role',
  'species',
  'name',
  'age',
  'gender',
  'photo',
  'relation',
];

/**
 * Ordered steps for a role. `isFirst=true` OMITS the `relation` step
 * (the first cast entry has no one to relate to). `isFirst=false`
 * inserts `relation` between `gender` and `photo`.
 */
export function stepsForRole(role: FlowRole, isFirst: boolean): readonly FlowStep[] {
  if (role === 'tier') return isFirst ? STEP_ORDER_TIER_FIRST : STEP_ORDER_TIER_NON_FIRST;
  return isFirst ? STEP_ORDER_PERSON_FIRST : STEP_ORDER_PERSON_NON_FIRST;
}

/**
 * Given the current step, role, and isFirst, return the next step.
 * When the current step is the last one, returns 'complete'.
 * `edit_review` is terminal and returns itself.
 */
export function nextStep(current: FlowStep, role: FlowRole, isFirst: boolean): FlowStep {
  if (current === 'complete' || current === 'edit_review') return current;
  const steps = stepsForRole(role, isFirst);
  const idx = steps.indexOf(current);
  if (idx === -1) return steps[0]!;
  if (idx >= steps.length - 1) return 'complete';
  return steps[idx + 1]!;
}

/**
 * First unanswered step for an entry, given the current field values +
 * face photo state + whether the role hint has been explicitly set
 * (i.e. the customer picked at Q_role). Returns 'complete' iff every
 * required answer is present. `relation` is only ever asked for
 * non-first entries, AFTER `photo`. On hydration we cannot distinguish
 * "skipped" from "not yet asked" — so a persisted non-first entry
 * with a photo but empty personenNotiz hydrates as `complete`. Rare
 * edge case (page reload mid-flow); the trade-off is not bouncing
 * a customer back to a question they may have already skipped.
 */
export function firstUnansweredStep(
  entry: CastTextEntry,
  role: FlowRole,
  hasFacePhoto: boolean,
  roleWasPicked: boolean,
  isFirst: boolean,
): FlowStep {
  const steps = stepsForRole(role, isFirst);
  for (const step of steps) {
    if (!isStepAnswered(step, entry, hasFacePhoto, roleWasPicked)) return step;
  }
  return 'complete';
}

function isStepAnswered(
  step: FlowStep,
  entry: CastTextEntry,
  hasFacePhoto: boolean,
  roleWasPicked: boolean,
): boolean {
  switch (step) {
    case 'role':
      // 'role' is answered iff the customer explicitly picked at Q_role
      // (tracked in `roleByKey`) OR — at hydration — the entry has
      // enough data (personType, name/age/photo) that a role pick is
      // implied by the persisted shape.
      return roleWasPicked;
    case 'species':
      return entry.species.trim().length > 0;
    case 'name':
      return entry.name.trim().length > 0;
    case 'age':
      return entry.ageYears !== null;
    case 'gender':
      return entry.geschlecht !== '';
    case 'relation':
      // Round-2 rev: relation is asked AFTER photo. On hydration we
      // cannot tell "skipped" from "not asked yet" — treat photo-
      // present as evidence the customer advanced past this step
      // (they either answered or skipped Q_relation). Live flow
      // relies on activeStepByKey to route to `relation` after K2
      // close, so the hydration heuristic only matters for a page
      // reload with a persisted photo.
      return entry.personenNotiz.trim().length > 0 || hasFacePhoto;
    case 'photo':
      return hasFacePhoto;
    case 'complete':
    case 'edit_review':
      return true;
  }
}

/**
 * Infer a role hint from a persisted entry — used at hydration only.
 *
 * Returns null when the entry carries no role signal (fresh seed:
 * `personType='person'` + `ageYears=null`). F17 (Sebastian field
 * finding 2026-10-17): the pre-fix `return 'erwachsener'` fallback
 * silently preselected "Einen Erwachsenen" on every new book — the
 * aria-pressed=true attribute was set from the first render and,
 * under F7's BUTTON_CHOICE pressed styling (0ca9c21), the button
 * also rendered dark-navy.
 *
 * - 'tier' when personType is tier (set only by explicit pick).
 * - 'kind' when ageYears is a child age (<18).
 * - 'erwachsener' when ageYears is an adult age (>=18).
 * - null otherwise — the caller treats the entry as "role not
 *   yet picked" and RoleQuestion renders with no button pressed.
 */
function inferRoleFromEntry(entry: CastTextEntry): FlowRole | null {
  if (entry.personType === 'tier') return 'tier';
  if (entry.ageYears !== null && entry.ageYears < 18) return 'kind';
  if (entry.ageYears !== null) return 'erwachsener';
  return null;
}

/**
 * All required steps for this entry answered? Drives the Fertig button
 * in edit-review mode.
 */
function isEntryComplete(
  entry: CastTextEntry,
  role: FlowRole,
  hasFacePhoto: boolean,
  isFirst: boolean,
): boolean {
  const steps = stepsForRole(role, isFirst);
  for (const step of steps) {
    // Role is answered by construction inside edit-review (the entry
    // reached this state via `complete` → Ändern), so we treat it as
    // answered regardless of `roleWasPicked` state.
    if (step === 'role') continue;
    // Relation is optional (Überspringen allowed) — never blocks
    // Fertig.
    if (step === 'relation') continue;
    if (!isStepAnswered(step, entry, hasFacePhoto, true)) return false;
  }
  return true;
}

// ─── Styling constants (mirror PersonenRoute) ──────────────────────

const BUTTON_GOLD =
  'h-12 w-full rounded-lg bg-dream-gold px-8 text-base font-bold text-dream-navy hover:bg-dream-gold/90 disabled:opacity-60';
const BUTTON_OUTLINE_SM =
  'h-11 rounded-lg border-[1.5px] border-dream-navy bg-dream-white px-4 text-sm font-semibold text-dream-navy disabled:opacity-50';
// F7 (Sebastian field finding 2026-10-16) — visible pressed state so
// a customer can see which choice already holds the current value.
// `aria-pressed=true` on any consumer of BUTTON_CHOICE (RoleQuestion,
// GenderQuestion) now flips the fill to dream-navy + text to white.
// EmptyStateRolePicker + the "add another" picker omit `aria-pressed`
// entirely (nothing is selected on those surfaces), so they continue
// to render in the unpressed white style unchanged.
//
// Exported for the button-choice-pressed.test.tsx spec that
// renders a plain button and asserts the pressed-state Tailwind
// variants are present in the className string.
export const BUTTON_CHOICE =
  'h-11 w-full rounded-lg border-[1.5px] border-dream-navy bg-dream-white px-4 text-base font-semibold text-dream-navy hover:bg-dream-background focus-visible:outline-2 focus-visible:outline-dream-navy/50 aria-pressed:bg-dream-navy aria-pressed:text-dream-white';
const FIELD_CLASS = 'builder-field h-11 w-full px-3 text-base text-dream-navy outline-none';
const TEXTAREA_CLASS =
  'builder-field w-full px-3 py-2 text-base text-dream-navy outline-none resize-none';

// ─── Main component ───────────────────────────────────────────────

export function PersonenFlow(props: PersonenFlowProps) {
  const {
    cast,
    addCast,
    updateCast,
    removeCast,
    photos,
    setFacePhoto,
    onFacePickedForK2,
    k2OpenKey,
    onAllCastComplete,
    onFirstPersonComplete,
    onFirstPhotoStepReached,
    onRecipientChosen,
    onPersonNameEntered,
    onPersonCompleted,
  } = props;

  // Per-entry active step. Missing entries → default to first-unanswered
  // (or 'role' for a freshly-added entry).
  const [activeStepByKey, setActiveStepByKey] = useState<ReadonlyMap<string, FlowStep>>(
    () => new Map(),
  );

  // Per-entry role hint. Freshly added entries have no hint until the
  // customer picks at Q1; the store's default personType='person' is
  // ambiguous between 'kind' and 'erwachsener' — we only commit the
  // role hint when the customer picks.
  const [roleByKey, setRoleByKey] = useState<ReadonlyMap<string, FlowRole>>(() => new Map());

  // Sebastian ruling 2026-09-24 (round 2) — entries currently in
  // "edit-review" mode (reopened via Ändern on a collapsed card). In
  // edit mode:
  //   - Doch nicht is HIDDEN (only Entfernen can delete a person).
  //   - Fertig button appears at the bottom, disabled until every
  //     field is complete.
  //   - Advancing from a specific step returns to `edit_review`
  //     (not to the next unanswered step), so the customer can tweak
  //     one field at a time.
  const [editingKeys, setEditingKeys] = useState<ReadonlySet<string>>(() => new Set());

  // Ref map for scroll-into-view; keyed by entry key + step so a new
  // active step can scroll after mount.
  const activeStepRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  // Track which entry keys we've seen K2 open for; when it closes AND
  // the entry has a face photo, we advance to complete (or edit_review
  // if the entry is in edit mode).
  const previousK2OpenKey = useRef<string | null>(null);

  // ─── Hydration on mount + when cast changes shape ───────────────
  //
  // For entries not tracked in `activeStepByKey`, infer initial state:
  // complete iff name + age + face photo; otherwise first unanswered.
  //
  // Reload-trap fix (Sebastian ruling 2026-10-09 09:38): ALSO elevate
  // an already-tracked entry from `'photo'` to `'complete'` once the
  // face photo arrives from IDB. The pre-fix path tracked each entry
  // once at mount with photos empty (because IDB hydration is async),
  // then never re-derived — the trap on reload. Elevation is
  // conservative: only `'photo'` → `'complete'`, only when every
  // required field is already present, AND K2 is NOT currently open
  // for this entry (the in-flow photo pick path owns its own
  // transition via the K2-close effect below — must not race it).
  useEffect(() => {
    setActiveStepByKey((prev) => {
      let changed = false;
      const next = new Map(prev);
      // F6 (Sebastian field finding 2026-10-16) — W29 "one decision
      // per step": the configurator can have at most ONE unfinished
      // active entry at a time. During hydration, grant an active
      // step to a new entry ONLY if there is no other unfinished
      // entry (either already-tracked or just-granted this pass).
      // Second and later untracked unfinished entries stay
      // untracked; the render branch returns null for them. When
      // the single active entry transitions to 'complete' via a
      // later state update, this effect runs again (cast + photos
      // are deps + activeStepByKey setState triggers a re-render
      // that re-evaluates the for-loop) and the next untracked
      // entry claims the slot.
      //
      // Pre-fix path (observed in the probe-transfer F6 bug):
      // 2+ probe figures seeded at once → every entry hydrated
      // in the same pass → multiple ActiveFlowCards rendered
      // simultaneously, violating W29.
      //
      // Seed from `next` so an already-tracked unfinished entry
      // from a previous pass still blocks a new one — a later
      // effect-trigger (e.g. photos change) must not promote a
      // deferred entry while the earlier one is still mid-flow.
      let hasUnfinishedActiveEntry = false;
      for (const [, step] of next) {
        if (step !== 'complete' && step !== 'edit_review') {
          hasUnfinishedActiveEntry = true;
          break;
        }
      }
      for (const entry of cast) {
        const facePresent = (photos.get(entry.key)?.face ?? null) !== null;
        if (next.has(entry.key)) {
          // Reload-trap: elevate stale `'photo'` to `'complete'` when
          // the IDB-hydrated face arrives — only outside the live K2
          // confirm flow, which owns the in-flow transition.
          //
          // F13 follow-up (2026-10-17) — also skip the elevation
          // when the customer has EXPLICITLY jumped to the photo
          // step from edit_review (`isEditing && activeStep ===
          // 'photo'`). Pre-F13 the hydration effect only ran on
          // cast/photos/role/k2 changes; the activeStep flip
          // from edit_review → photo did not re-run the effect,
          // so the W34 "Ändern on photo row → Fertig to go back"
          // flow worked. F13 added `activeStepByKey` to the deps
          // so card 2 opens after card 1 completes (probe-
          // transfer field case) — but THAT change caused the
          // Ändern-on-photo trigger to re-run the reload-trap
          // and auto-elevate back to 'complete', hiding the W34
          // Fertig button. Gate the elevation on
          // `!editingKeys.has(entry.key)` to preserve W34.
          if (
            k2OpenKey !== entry.key &&
            !editingKeys.has(entry.key) &&
            next.get(entry.key) === 'photo' &&
            facePresent &&
            entry.name.trim().length > 0 &&
            entry.ageYears !== null &&
            entry.geschlecht !== '' &&
            (entry.personType !== 'tier' || entry.species.trim().length > 0)
          ) {
            next.set(entry.key, 'complete');
            changed = true;
          }
          continue;
        }
        // At hydration: consider role-picked iff the persisted entry
        // has enough evidence a real role was chosen — a name or age
        // or a face photo, OR personType is 'tier' (which is only set
        // by an explicit pick). Fresh seeded entries have none of
        // these and thus start at Q_role.
        const rolePicked =
          entry.personType === 'tier' ||
          entry.name.trim().length > 0 ||
          entry.ageYears !== null ||
          facePresent;
        // F17 (2026-10-17): inferRoleFromEntry now returns null for
        // fresh entries. In that path firstUnansweredStep short-
        // circuits on `roleWasPicked=false` and returns 'role' at
        // the first iteration without reading the `role` arg, so a
        // dummy fallback is safe + does not leak to the UI.
        const role = roleByKey.get(entry.key) ?? inferRoleFromEntry(entry) ?? 'erwachsener';
        const isFirst = cast.indexOf(entry) === 0;
        const step = firstUnansweredStep(entry, role, facePresent, rolePicked, isFirst);
        // F6: 'complete' entries carry no "mid-flow" burden — they
        // can be hydrated in bulk without opening cards. Only an
        // UNFINISHED entry claims the single mid-flow slot.
        if (step !== 'complete') {
          if (hasUnfinishedActiveEntry) {
            // Another entry (tracked from a previous pass OR
            // granted earlier this pass) already holds the single
            // mid-flow slot. Leave this one untracked — the render
            // branch skips untracked keys, and the next pass (after
            // the active entry completes) picks it up.
            continue;
          }
          hasUnfinishedActiveEntry = true;
        }
        next.set(entry.key, step);
        changed = true;
      }
      // Drop entries that no longer exist in cast.
      for (const key of next.keys()) {
        if (!cast.some((c) => c.key === key)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setRoleByKey((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const entry of cast) {
        if (next.has(entry.key)) continue;
        // F17 fix (2026-10-17) — only seed roleByKey when the
        // persisted entry carries a real role signal. A fresh seed
        // entry (`personType='person'` + `ageYears=null`) stays
        // UNTRACKED so `roleByKey.has(entry.key)` returns false,
        // which drives RoleQuestion's `rolePicked=false` and keeps
        // every button in the unpressed state until the customer
        // actually clicks.
        const inferred = inferRoleFromEntry(entry);
        if (inferred === null) continue;
        next.set(entry.key, inferred);
        changed = true;
      }
      for (const key of next.keys()) {
        if (!cast.some((c) => c.key === key)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setEditingKeys((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const key of prev) {
        if (!cast.some((c) => c.key === key)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // F13 (Sebastian field finding 2026-10-17 / 2026-10-16 F6
    // regression) — `activeStepByKey` MUST be in the deps so the
    // effect re-runs when the single-slot entry transitions to
    // 'complete' AND when a late K2 close flips a key. Without
    // this dep, card 2 (the deferred untracked entry) would
    // never get promoted after card 1 completes — the dead-end
    // Sebastian reproduced on buch65@dreamable.kids.
    //
    // Infinite-loop safety: the inner `setActiveStepByKey`
    // callback returns `prev` unchanged when nothing to do;
    // React's identity check bails out of the re-render, so the
    // effect does not re-fire until a real external dep
    // (cast, photos, roleByKey, k2OpenKey) OR an actual map
    // mutation (new 'complete' transition from the question
    // flow) occurs.
  }, [cast, photos, roleByKey, k2OpenKey, activeStepByKey, editingKeys]);

  // ─── K2 close → advance-to-relation / complete / edit_review ────
  //
  // K2 confirm ("Passt so") means the customer accepted the photo.
  // From there:
  //   - edit mode → return to `edit_review` (single-field tweak
  //     stays inside review).
  //   - first entry (index 0), non-edit → `complete` directly.
  //     No relationship question for the first person.
  //   - non-first entry, non-edit → `relation`. Photo is the core;
  //     the relationship question follows as a light closer
  //     (Sebastian ruling 2026-09-24 revised).
  useEffect(() => {
    const previous = previousK2OpenKey.current;
    previousK2OpenKey.current = k2OpenKey;
    if (previous !== null && k2OpenKey === null) {
      const facePresent = (photos.get(previous)?.face ?? null) !== null;
      if (facePresent) {
        setActiveStepByKey((prev) => {
          let target: FlowStep;
          if (editingKeys.has(previous)) {
            target = 'edit_review';
          } else {
            const entry = cast.find((c) => c.key === previous);
            const isFirst = entry !== undefined && cast.indexOf(entry) === 0;
            target = isFirst ? 'complete' : 'relation';
          }
          if (prev.get(previous) === target) return prev;
          const next = new Map(prev);
          next.set(previous, target);
          return next;
        });
      }
    }
  }, [k2OpenKey, photos, editingKeys, cast]);

  // ─── Signal onFirstPersonComplete when any entry transitions ────
  //
  // We fire the signal every time we detect a fresh transition to
  // `complete` — the parent's sessionStorage dedup ensures phCapture
  // fires ONCE. The parallel `completedKeysRef` tracks WHICH entries
  // have transitioned so we can fire the per-entry
  // `onPersonCompleted` callback once per key, without re-firing on
  // re-renders.
  const previousCompleteCountRef = useRef<number>(0);
  const completedKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const currentComplete = Array.from(activeStepByKey.values()).filter(
      (s) => s === 'complete',
    ).length;
    if (currentComplete > previousCompleteCountRef.current && currentComplete >= 1) {
      onFirstPersonComplete();
    }
    previousCompleteCountRef.current = currentComplete;
    for (const [entryKey, step] of activeStepByKey) {
      if (step !== 'complete') continue;
      if (completedKeysRef.current.has(entryKey)) continue;
      completedKeysRef.current.add(entryKey);
      const castIndex = cast.findIndex((c) => c.key === entryKey);
      if (castIndex >= 0) {
        onPersonCompleted(entryKey, castIndex, cast.length);
      }
    }
  }, [activeStepByKey, cast, onFirstPersonComplete, onPersonCompleted]);

  // ─── Signal onFirstPhotoStepReached ────────────────────────────
  //
  // Fires whenever any entry's active step transitions to 'photo'.
  const photoStepEverSeenRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const [entryKey, step] of activeStepByKey) {
      if (step === 'photo' && !photoStepEverSeenRef.current.has(entryKey)) {
        photoStepEverSeenRef.current.add(entryKey);
        onFirstPhotoStepReached();
      }
    }
  }, [activeStepByKey, onFirstPhotoStepReached]);

  // ─── Keyboard-jump scroll-into-view fix ──────────────────────────
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const raf = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        for (const entry of cast) {
          const step = activeStepByKey.get(entry.key);
          if (step !== undefined && step !== 'complete') {
            const el = activeStepRefs.current.get(`${entry.key}:${step}`);
            if (el && typeof el.scrollIntoView === 'function') {
              try {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              } catch {
                // jsdom may throw on scrollIntoView options — non-fatal.
              }
            }
            break;
          }
        }
      }, 120);
    });
    return () => {
      cancelAnimationFrame(raf);
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [activeStepByKey, cast]);

  // ─── Handlers ───────────────────────────────────────────────────

  const setActive = useCallback((entryKey: string, step: FlowStep) => {
    setActiveStepByKey((prev) => {
      const next = new Map(prev);
      next.set(entryKey, step);
      return next;
    });
  }, []);

  const setRole = useCallback((entryKey: string, role: FlowRole) => {
    setRoleByKey((prev) => {
      const next = new Map(prev);
      next.set(entryKey, role);
      return next;
    });
  }, []);

  const addEditing = useCallback((entryKey: string) => {
    setEditingKeys((prev) => {
      if (prev.has(entryKey)) return prev;
      const next = new Set(prev);
      next.add(entryKey);
      return next;
    });
  }, []);

  const removeEditing = useCallback((entryKey: string) => {
    setEditingKeys((prev) => {
      if (!prev.has(entryKey)) return prev;
      const next = new Set(prev);
      next.delete(entryKey);
      return next;
    });
  }, []);

  /**
   * Advance from the current step. Called from every step's
   * advance-handler after the customer confirms an answer.
   *
   * Sebastian ruling 2026-09-24 (round 2) — behaviour splits by mode:
   *   - FIRST-ENTRY mode (entry NOT in editingKeys) → advance to
   *     the next step in role order, exactly like before. If the
   *     next step is 'complete', the K2-close effect (photo) or
   *     the natural transition handles the collapse.
   *   - EDIT-REVIEW mode (entry IS in editingKeys) → return to
   *     `edit_review` so the customer sees the compact review with
   *     the just-edited value visible and Fertig ready.
   */
  const advanceFromCurrent = useCallback(
    (entryKey: string) => {
      const currentStep = activeStepByKey.get(entryKey);
      if (
        currentStep === undefined ||
        currentStep === 'complete' ||
        currentStep === 'edit_review'
      ) {
        return;
      }
      const entry = cast.find((c) => c.key === entryKey);
      if (entry === undefined) return;
      // Analytics (Sebastian ruling 2026-10-08) — advancing past
      // `name` means the customer confirmed a non-empty name (the
      // NameQuestion component disables Weiter until the input is
      // valid). Parent dedups per entryKey, so re-entering the name
      // step via edit-review will not re-fire.
      if (currentStep === 'name') {
        const castIndex = cast.indexOf(entry);
        onPersonNameEntered(entryKey, castIndex);
      }
      if (editingKeys.has(entryKey)) {
        setActive(entryKey, 'edit_review');
        return;
      }
      // F17 (2026-10-17): advanceFromCurrent runs AFTER a role was
      // picked (we're past the 'role' step), so roleByKey.has is
      // true. The inference fallback stays as a defensive 'erwachsener'
      // for the unreachable "advance from a step past role without
      // roleByKey entry" case.
      const role = roleByKey.get(entryKey) ?? inferRoleFromEntry(entry) ?? 'erwachsener';
      const isFirst = cast.indexOf(entry) === 0;
      const next = nextStep(currentStep, role, isFirst);
      setActive(entryKey, next);
    },
    [activeStepByKey, cast, editingKeys, onPersonNameEntered, roleByKey, setActive],
  );

  // Empty-state "Wen möchtest du hinzufügen?" → spawn a fresh entry
  // AND set its role + advance to whichever step is next after 'role'.
  const spawnNewEntryAtRole = useCallback(
    (role: FlowRole) => {
      const isFirst = cast.length === 0;
      const newKey = addCast();
      setRole(newKey, role);
      // Analytics (Sebastian ruling 2026-10-08) — role pick on a
      // freshly-added entry. New entry's castIndex is `cast.length`
      // before this update commits. Parent dedups per entryKey, so a
      // later role change on the same entry via Q_role does not
      // re-fire.
      onRecipientChosen(newKey, role, cast.length);
      // For tier the store's personType default 'person' needs a flip.
      if (role === 'tier') {
        updateCast(newKey, {
          personType: 'tier',
          // Sebastian ruling: flipping to Tier auto-fills geschlecht
          // 'keineAngabe' — but for the flow, we KEEP gender as an
          // active question so the customer picks explicitly. Reset
          // to '' so we ask.
          geschlecht: '',
        });
      }
      // Advance to first step after 'role'.
      setActiveStepByKey((prev) => {
        const next = new Map(prev);
        next.set(newKey, nextStep('role', role, isFirst));
        return next;
      });
    },
    [addCast, cast.length, onRecipientChosen, setRole, updateCast],
  );

  // Change role on an existing entry (from Q_role). Advance to next.
  const chooseRole = useCallback(
    (entryKey: string, role: FlowRole) => {
      setRole(entryKey, role);
      // Analytics (Sebastian ruling 2026-10-08) — role pick on an
      // existing entry (seeded cast[0] on fresh mount, OR edit-review
      // role change). Parent dedups per entryKey so edit-review
      // role changes do not re-fire.
      const entryIndex = cast.findIndex((c) => c.key === entryKey);
      if (entryIndex >= 0) {
        onRecipientChosen(entryKey, role, entryIndex);
      }
      if (role === 'tier') {
        updateCast(entryKey, { personType: 'tier' });
      } else {
        updateCast(entryKey, { personType: 'person' });
      }
      // The advance from role obeys the edit-mode branch — a role
      // change during edit returns to edit_review.
      if (editingKeys.has(entryKey)) {
        setActive(entryKey, 'edit_review');
        return;
      }
      const isFirst = entryIndex === 0;
      setActive(entryKey, nextStep('role', role, isFirst));
    },
    [cast, editingKeys, onRecipientChosen, setRole, updateCast, setActive],
  );

  // ─── Rendering ──────────────────────────────────────────────────

  const anyEntryInFlow = cast.some((c) => {
    const s = activeStepByKey.get(c.key);
    return s !== 'complete' && s !== 'edit_review';
  });

  const allComplete =
    cast.length > 0 &&
    cast.every((c) => {
      const s = activeStepByKey.get(c.key);
      return s === 'complete' || s === 'edit_review';
    });

  const canAddMore = cast.length < CAST_MAX_ENTRIES;

  // Empty-cast layout: render Q_role as the very first question. When
  // the customer taps a role, we spawn the entry via addCast() +
  // advance past 'role'.
  if (cast.length === 0) {
    return (
      <div className="space-y-4">
        <EmptyStateRolePicker onPick={spawnNewEntryAtRole} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {cast.map((entry) => {
        // F6 — an untracked entry means the hydration effect
        // deferred its activation (W29 one-at-a-time). Render
        // NOTHING; the effect runs again when the earlier entry
        // completes and assigns a step to this one.
        const trackedStep = activeStepByKey.get(entry.key);
        if (trackedStep === undefined) {
          return null;
        }
        const step = trackedStep;
        // F17 (2026-10-17) — `rolePicked` is TRUE only when the
        // customer has explicitly chosen at Q_role (roleByKey.has)
        // OR a hydrated entry carried a non-null role signal
        // (populated into roleByKey by the hydration effect above).
        // RoleQuestion gates `aria-pressed` on this boolean so a
        // fresh entry renders with no button in the F7-styled
        // pressed state. The `role` fallback stays — downstream
        // step logic (stepsForRole / NameQuestion / etc.) only
        // reads it at steps past 'role' where roleByKey is set.
        const rolePicked = roleByKey.has(entry.key);
        const role = roleByKey.get(entry.key) ?? inferRoleFromEntry(entry) ?? 'erwachsener';
        const entryPhotos = photos.get(entry.key) ?? { face: null };
        const castIndex = cast.indexOf(entry);
        const isFirst = castIndex === 0;
        const isEditing = editingKeys.has(entry.key);
        if (step === 'complete') {
          // W29 (Sebastian ruling 2026-10-14): hide Ändern + Entfernen
          // on this collapsed card while ANY other entry is mid-flow
          // (not complete). "Mid-flow" includes a sister entry still
          // walking the question flow AND a sister entry currently in
          // edit_review — editing one person while another is half-
          // done opens two cards at once and violates "one decision
          // per step". Entries in `complete` state do not count — a
          // room of CollapsedCards is a valid stationary state and
          // every one of them must stay editable.
          const otherEntryInFlow = cast.some(
            (c) => c.key !== entry.key && activeStepByKey.get(c.key) !== 'complete',
          );
          return (
            <CollapsedCard
              key={entry.key}
              entry={entry}
              facePhoto={entryPhotos.face?.file ?? null}
              showEditActions={!otherEntryInFlow}
              onEdit={() => {
                // Sebastian ruling 2026-09-24 (round 2): Ändern on a
                // collapsed card enters edit-review mode instead of
                // walking the customer through the whole flow again.
                addEditing(entry.key);
                setActive(entry.key, 'edit_review');
              }}
              onRemove={() => {
                setActiveStepByKey((prev) => {
                  const next = new Map(prev);
                  next.delete(entry.key);
                  return next;
                });
                setRoleByKey((prev) => {
                  const next = new Map(prev);
                  next.delete(entry.key);
                  return next;
                });
                removeEditing(entry.key);
                removeCast(entry.key);
              }}
            />
          );
        }
        // Mid-entry Doch nicht is only shown for NEW entries (index > 0
        // AND not in edit mode). An editing customer uses Entfernen.
        const cancelHandler =
          castIndex > 0 && !isEditing
            ? () => {
                setActiveStepByKey((prev) => {
                  const next = new Map(prev);
                  next.delete(entry.key);
                  return next;
                });
                setRoleByKey((prev) => {
                  const next = new Map(prev);
                  next.delete(entry.key);
                  return next;
                });
                removeCast(entry.key);
              }
            : null;
        return (
          <ActiveFlowCard
            key={entry.key}
            entry={entry}
            castIndex={castIndex}
            isFirst={isFirst}
            isEditing={isEditing}
            role={role}
            rolePicked={rolePicked}
            activeStep={step}
            photos={entryPhotos}
            registerActiveStepRef={(refStep, node) =>
              activeStepRefs.current.set(`${entry.key}:${refStep}`, node)
            }
            onJumpToStep={(target) => setActive(entry.key, target)}
            onCommitEdit={() => {
              // Fertig: leave edit mode, collapse to complete.
              removeEditing(entry.key);
              setActive(entry.key, 'complete');
            }}
            onChooseRole={(r) => chooseRole(entry.key, r)}
            onUpdateSpecies={(text) => updateCast(entry.key, { species: text })}
            onUpdateName={(text) => updateCast(entry.key, { name: text })}
            onUpdateAge={(age) => updateCast(entry.key, { ageYears: age })}
            onUpdateGeschlecht={(g) => updateCast(entry.key, { geschlecht: g })}
            onUpdateRelation={(text) => updateCast(entry.key, { personenNotiz: text })}
            onSpeciesAdvance={() => advanceFromCurrent(entry.key)}
            onNameAdvance={() => advanceFromCurrent(entry.key)}
            onAgeAdvance={() => advanceFromCurrent(entry.key)}
            onGenderAdvance={() => advanceFromCurrent(entry.key)}
            onRelationAdvance={() => advanceFromCurrent(entry.key)}
            onRelationSkip={() => {
              // Skip clears the note (if any) and advances.
              updateCast(entry.key, { personenNotiz: '' });
              advanceFromCurrent(entry.key);
            }}
            onFacePicked={(file) => {
              setFacePhoto(entry.key, file);
              onFacePickedForK2(entry.key);
            }}
            onFaceRemoved={() => setFacePhoto(entry.key, null)}
            {...(cancelHandler !== null ? { onCancelMidEntry: cancelHandler } : {})}
          />
        );
      })}

      {/* "Kommt noch jemand?" — rendered iff every entry is complete
          or in edit-review AND at least one entry exists AND no entry
          is mid-flow. Edit-review is a stationary state, so we allow
          the prompt to show — the customer can still Fertig later. */}
      {allComplete && !anyEntryInFlow && (
        <AddAnotherPrompt
          canAddMore={canAddMore}
          onAddAnother={(role) => spawnNewEntryAtRole(role)}
          onFinish={onAllCastComplete}
        />
      )}
    </div>
  );
}

// ─── Empty-state Q1 picker ─────────────────────────────────────────

interface EmptyStateRolePickerProps {
  readonly onPick: (role: FlowRole) => void;
}

function EmptyStateRolePicker({ onPick }: EmptyStateRolePickerProps) {
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-dream-navy">
        {STRINGS.book.personen.qRoleTitle}
      </h3>
      <div className="flex flex-col gap-2">
        <button type="button" onClick={() => onPick('kind')} className={BUTTON_CHOICE}>
          {STRINGS.book.personen.qRoleKind}
        </button>
        <button type="button" onClick={() => onPick('erwachsener')} className={BUTTON_CHOICE}>
          {STRINGS.book.personen.qRoleErwachsener}
        </button>
        <button type="button" onClick={() => onPick('tier')} className={BUTTON_CHOICE}>
          {STRINGS.book.personen.qRoleTier}
        </button>
      </div>
    </div>
  );
}

// ─── Collapsed card ────────────────────────────────────────────────

interface CollapsedCardProps {
  readonly entry: CastTextEntry;
  /**
   * F8 (Sebastian field finding 2026-10-16) — the entry's face
   * photo from the parent's photo map, if any. Rendered as a small
   * (44 px) thumbnail next to the ✓ icon so a customer who got
   * here via probe-transfer can see that the photo they uploaded
   * on /figuren is actually attached. null → no thumbnail.
   */
  readonly facePhoto: File | null;
  readonly onEdit: () => void;
  readonly onRemove: () => void;
  /**
   * W29 gate. False while another entry is mid-flow (active or in
   * edit_review). When false, Ändern + Entfernen are not rendered —
   * the customer must finish the other entry first.
   */
  readonly showEditActions: boolean;
}

export function CollapsedCard({
  entry,
  facePhoto,
  onEdit,
  onRemove,
  showEditActions,
}: CollapsedCardProps) {
  // Derive a transient object-URL for the <img> src. Revoke on
  // unmount so the Blob reference can be GC'd. The empty-dep
  // `useEffect` would be a stale closure if `facePhoto` changed,
  // so we gate on identity + run the cleanup on every change.
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  useEffect(() => {
    if (facePhoto === null) {
      setPhotoUrl(null);
      return;
    }
    const url = URL.createObjectURL(facePhoto);
    setPhotoUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [facePhoto]);
  const altText = STRINGS.book.personen.collapsedPhotoAlt.replace('[[name]]', entry.name);

  return (
    <div className="rounded-lg border border-dream-navy/15 bg-dream-white p-4 space-y-3">
      <div className="flex items-center gap-3">
        {photoUrl !== null ? (
          <img
            src={photoUrl}
            alt={altText}
            className="h-11 w-11 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span aria-hidden className="text-lg font-semibold text-dream-navy">
            ✓
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="truncate text-base font-semibold text-dream-navy">
            {entry.name}
            {entry.ageYears !== null && (
              <>
                {', '}
                {entry.ageYears} {STRINGS.book.personen.collapsedAgeSuffix}
              </>
            )}
          </p>
        </div>
        {showEditActions && (
          <>
            <button
              type="button"
              onClick={onEdit}
              className="text-sm text-dream-navy underline focus-visible:outline-2 focus-visible:outline-dream-navy/50"
            >
              {STRINGS.book.personen.collapsedEdit}
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="text-xs text-dream-navy/70 underline focus-visible:outline-2 focus-visible:outline-dream-navy/50"
            >
              {STRINGS.book.personen.collapsedRemove}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Active-flow card ──────────────────────────────────────────────

interface ActiveFlowCardProps {
  readonly entry: CastTextEntry;
  readonly castIndex: number;
  readonly isFirst: boolean;
  readonly isEditing: boolean;
  readonly role: FlowRole;
  /**
   * F17 (2026-10-17) — true only when the customer explicitly
   * picked at Q_role OR the hydrated entry carried a non-null
   * role signal. Threaded to RoleQuestion so `aria-pressed` on
   * the three role buttons renders TRUE only after a real pick,
   * never from a hydration fallback.
   */
  readonly rolePicked: boolean;
  readonly activeStep: FlowStep;
  readonly photos: CastPhotos;
  readonly registerActiveStepRef: (step: FlowStep, node: HTMLDivElement | null) => void;
  readonly onJumpToStep: (target: FlowStep) => void;
  readonly onCommitEdit: () => void;
  readonly onChooseRole: (role: FlowRole) => void;
  readonly onUpdateSpecies: (text: string) => void;
  readonly onUpdateName: (text: string) => void;
  readonly onUpdateAge: (age: number | null) => void;
  readonly onUpdateGeschlecht: (g: CastGeschlecht) => void;
  readonly onUpdateRelation: (text: string) => void;
  readonly onSpeciesAdvance: () => void;
  readonly onNameAdvance: () => void;
  readonly onAgeAdvance: () => void;
  readonly onGenderAdvance: () => void;
  readonly onRelationAdvance: () => void;
  readonly onRelationSkip: () => void;
  readonly onFacePicked: (file: File) => void;
  readonly onFaceRemoved: () => void;
  readonly onCancelMidEntry?: () => void;
}

function ActiveFlowCard(props: ActiveFlowCardProps) {
  const {
    entry,
    castIndex,
    isFirst,
    isEditing,
    role,
    rolePicked,
    activeStep,
    photos,
    registerActiveStepRef,
    onJumpToStep,
    onCommitEdit,
    onChooseRole,
    onUpdateSpecies,
    onUpdateName,
    onUpdateAge,
    onUpdateGeschlecht,
    onUpdateRelation,
    onSpeciesAdvance,
    onNameAdvance,
    onAgeAdvance,
    onGenderAdvance,
    onRelationAdvance,
    onRelationSkip,
    onFacePicked,
    onFaceRemoved,
    onCancelMidEntry,
  } = props;
  const steps = stepsForRole(role, isFirst);
  const facePresent = photos.face !== null;

  // Edit-review layout — show every answered step as a compact row,
  // plus the Fertig button. No active question rendered inline;
  // tapping Ändern on a row jumps to that step (which renders as a
  // normal single-question card until the customer advances again).
  if (activeStep === 'edit_review') {
    const fertigEnabled = isEntryComplete(entry, role, facePresent, isFirst);
    return (
      <div className="rounded-lg border border-dream-navy/15 p-4 space-y-4">
        {steps.map((s) => (
          <CompactAnsweredRow
            key={s}
            step={s}
            entry={entry}
            role={role}
            hasFacePhoto={facePresent}
            onEdit={() => onJumpToStep(s)}
          />
        ))}
        <div className="pt-2">
          <button
            type="button"
            onClick={onCommitEdit}
            disabled={!fertigEnabled}
            className={BUTTON_GOLD}
          >
            {STRINGS.book.personen.qCommonFertig}
          </button>
        </div>
      </div>
    );
  }

  const activeIdx = steps.indexOf(activeStep);
  const answeredBefore = activeIdx >= 0 ? steps.slice(0, activeIdx) : [];

  return (
    <div className="rounded-lg border border-dream-navy/15 p-4 space-y-4">
      {/* Compact answered-rows above active step */}
      {answeredBefore.map((step) => (
        <CompactAnsweredRow
          key={step}
          step={step}
          entry={entry}
          role={role}
          hasFacePhoto={facePresent}
          onEdit={() => onJumpToStep(step)}
        />
      ))}

      {/* Active step */}
      <div ref={(node) => registerActiveStepRef(activeStep, node)}>
        {activeStep === 'role' && (
          <RoleQuestion role={role} rolePicked={rolePicked} onPick={onChooseRole} />
        )}
        {activeStep === 'species' && (
          <SpeciesQuestion
            value={entry.species}
            onChange={onUpdateSpecies}
            onAdvance={onSpeciesAdvance}
          />
        )}
        {activeStep === 'name' && (
          <NameQuestion
            role={role}
            value={entry.name}
            onChange={onUpdateName}
            onAdvance={onNameAdvance}
          />
        )}
        {activeStep === 'age' && (
          <AgeQuestion
            name={entry.name}
            value={entry.ageYears}
            onChange={onUpdateAge}
            onAdvance={onAgeAdvance}
          />
        )}
        {activeStep === 'gender' && (
          <GenderQuestion
            name={entry.name}
            value={entry.geschlecht}
            onPick={(g) => {
              onUpdateGeschlecht(g);
              onGenderAdvance();
            }}
          />
        )}
        {activeStep === 'relation' && (
          <RelationQuestion
            role={role}
            name={entry.name}
            value={entry.personenNotiz}
            onChange={onUpdateRelation}
            onAdvance={onRelationAdvance}
            onSkip={onRelationSkip}
          />
        )}
        {activeStep === 'photo' && (
          <PhotoQuestion
            name={entry.name}
            photos={photos}
            onFacePicked={onFacePicked}
            onFaceRemoved={onFaceRemoved}
          />
        )}
      </div>

      {/* Edit-review re-entry button for the photo step. The photo
          question has no Weiter/Advance (K2 is the implicit advance,
          and K2 only fires on a NEW pick). Without this button a
          customer who jumped to photo from edit_review to inspect
          the picture had no way back — see W34 observation. The
          button returns to `edit_review` with the photo unchanged. */}
      {isEditing && activeStep === 'photo' && (
        <button type="button" onClick={() => onJumpToStep('edit_review')} className={BUTTON_GOLD}>
          {STRINGS.book.personen.qCommonFertig}
        </button>
      )}

      {/* "Doch nicht" mid-entry escape hatch — only for NEW entries
          at index > 0 that are NOT in edit mode. An editing customer
          uses Entfernen on the collapsed card. */}
      {onCancelMidEntry !== undefined && castIndex > 0 && !isEditing && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCancelMidEntry}
            className="text-sm text-dream-navy/70 underline"
          >
            {STRINGS.book.personen.midEntryCancel}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Compact answered row ──────────────────────────────────────────

interface CompactAnsweredRowProps {
  readonly step: FlowStep;
  readonly entry: CastTextEntry;
  readonly role: FlowRole;
  readonly hasFacePhoto: boolean;
  readonly onEdit: () => void;
}

function CompactAnsweredRow({ step, entry, role, hasFacePhoto, onEdit }: CompactAnsweredRowProps) {
  const label = compactAnsweredLabel(step, entry, role, hasFacePhoto);
  return (
    <div className="flex items-baseline gap-2 text-sm text-dream-navy/80">
      <span className="flex-1 truncate">{label}</span>
      <button type="button" onClick={onEdit} className="text-dream-navy underline">
        {STRINGS.book.personen.collapsedEdit}
      </button>
    </div>
  );
}

function compactAnsweredLabel(
  step: FlowStep,
  entry: CastTextEntry,
  role: FlowRole,
  hasFacePhoto: boolean,
): string {
  switch (step) {
    case 'role':
      if (role === 'kind') return STRINGS.book.personen.qRoleKind;
      if (role === 'erwachsener') return STRINGS.book.personen.qRoleErwachsener;
      return STRINGS.book.personen.qRoleTier;
    case 'species':
      return entry.species;
    case 'name':
      return entry.name;
    case 'age':
      return `${entry.ageYears ?? ''} ${STRINGS.book.personen.collapsedAgeSuffix}`;
    case 'gender':
      if (entry.geschlecht === 'maennlich') return STRINGS.book.personen.qGenderMale;
      if (entry.geschlecht === 'weiblich') return STRINGS.book.personen.qGenderFemale;
      if (entry.geschlecht === 'keineAngabe') return STRINGS.book.personen.qGenderNone;
      return '';
    case 'relation':
      return entry.personenNotiz.trim().length > 0
        ? entry.personenNotiz
        : STRINGS.book.personen.qRelationSkipped;
    case 'photo':
      return hasFacePhoto ? 'Foto ✓' : '';
    case 'complete':
    case 'edit_review':
      return '';
  }
}

// ─── Question renderers ────────────────────────────────────────────

interface RoleQuestionProps {
  readonly role: FlowRole;
  /**
   * F17 (2026-10-17) — gates `aria-pressed`. When false (fresh
   * entry, customer has not picked yet), every button renders
   * aria-pressed=false regardless of the inferred `role` fallback,
   * so under F7's BUTTON_CHOICE pressed-state styling no button
   * looks preselected.
   */
  readonly rolePicked: boolean;
  readonly onPick: (role: FlowRole) => void;
}

function RoleQuestion({ role, rolePicked, onPick }: RoleQuestionProps) {
  const pressed: FlowRole | null = rolePicked ? role : null;
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-dream-navy">
        {STRINGS.book.personen.qRoleTitle}
      </h3>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => onPick('kind')}
          className={BUTTON_CHOICE}
          aria-pressed={pressed === 'kind'}
        >
          {STRINGS.book.personen.qRoleKind}
        </button>
        <button
          type="button"
          onClick={() => onPick('erwachsener')}
          className={BUTTON_CHOICE}
          aria-pressed={pressed === 'erwachsener'}
        >
          {STRINGS.book.personen.qRoleErwachsener}
        </button>
        <button
          type="button"
          onClick={() => onPick('tier')}
          className={BUTTON_CHOICE}
          aria-pressed={pressed === 'tier'}
        >
          {STRINGS.book.personen.qRoleTier}
        </button>
      </div>
    </div>
  );
}

interface SpeciesQuestionProps {
  readonly value: string;
  readonly onChange: (text: string) => void;
  readonly onAdvance: () => void;
}

function SpeciesQuestion({ value, onChange, onAdvance }: SpeciesQuestionProps) {
  const canAdvance = value.trim().length > 0;
  const handleAdvance = () => {
    if (canAdvance) onAdvance();
  };
  return (
    <div className="space-y-2">
      <label className="block text-base font-semibold text-dream-navy">
        {STRINGS.book.personen.qSpeciesTitle}
      </label>
      <input
        type="text"
        className={FIELD_CLASS}
        maxLength={CAST_SPECIES_MAX_LENGTH}
        value={value}
        placeholder={STRINGS.book.personen.qSpeciesPlaceholder}
        aria-label={STRINGS.book.personen.qSpeciesTitle}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleAdvance();
          }
        }}
      />
      <button type="button" onClick={handleAdvance} disabled={!canAdvance} className={BUTTON_GOLD}>
        {STRINGS.book.personen.qCommonWeiter}
      </button>
    </div>
  );
}

interface NameQuestionProps {
  readonly role: FlowRole;
  readonly value: string;
  readonly onChange: (text: string) => void;
  readonly onAdvance: () => void;
}

function NameQuestion({ role, value, onChange, onAdvance }: NameQuestionProps) {
  const title =
    role === 'kind'
      ? STRINGS.book.personen.qNameKindTitle
      : role === 'erwachsener'
        ? STRINGS.book.personen.qNameErwachsenerTitle
        : STRINGS.book.personen.qNameTierTitle;
  const placeholder =
    role === 'kind'
      ? STRINGS.book.personen.qNameKindPlaceholder
      : role === 'erwachsener'
        ? STRINGS.book.personen.qNameErwachsenerPlaceholder
        : STRINGS.book.personen.qNameTierPlaceholder;
  const canAdvance = value.trim().length > 0;
  const handleAdvance = () => {
    if (canAdvance) onAdvance();
  };
  return (
    <div className="space-y-2">
      <label className="block text-base font-semibold text-dream-navy">{title}</label>
      <input
        type="text"
        className={FIELD_CLASS}
        value={value}
        placeholder={placeholder}
        aria-label={STRINGS.book.personen.nameAriaLabel}
        maxLength={CREATE_BOOK_ORDER_CAST_NAME_MAX_LENGTH}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleAdvance();
          }
        }}
      />
      <CharacterCounter
        value={value.length}
        corridor={{ min: 0, max: CREATE_BOOK_ORDER_CAST_NAME_MAX_LENGTH }}
      />
      <button type="button" onClick={handleAdvance} disabled={!canAdvance} className={BUTTON_GOLD}>
        {STRINGS.book.personen.qCommonWeiter}
      </button>
    </div>
  );
}

interface AgeQuestionProps {
  readonly name: string;
  readonly value: number | null;
  readonly onChange: (age: number | null) => void;
  readonly onAdvance: () => void;
}

function AgeQuestion({ name, value, onChange, onAdvance }: AgeQuestionProps) {
  const title = STRINGS.book.personen.qAgeTitle.replace(
    '{Name}',
    name.trim().length > 0 ? name.trim() : '',
  );
  const canAdvance = value !== null && Number.isFinite(value) && value >= 0 && value <= 120;
  const handleAdvance = () => {
    if (canAdvance) onAdvance();
  };
  return (
    <div className="space-y-2">
      <label className="block text-base font-semibold text-dream-navy">{title}</label>
      <input
        type="number"
        className={FIELD_CLASS}
        min={0}
        max={120}
        value={value ?? ''}
        placeholder={STRINGS.book.personen.qAgePlaceholder}
        aria-label={STRINGS.book.personen.alterAriaLabel}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            onChange(null);
            return;
          }
          const parsed = Number(raw);
          if (Number.isFinite(parsed)) onChange(parsed);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleAdvance();
          }
        }}
      />
      <button type="button" onClick={handleAdvance} disabled={!canAdvance} className={BUTTON_GOLD}>
        {STRINGS.book.personen.qCommonWeiter}
      </button>
    </div>
  );
}

interface GenderQuestionProps {
  readonly name: string;
  readonly value: CastGeschlecht;
  readonly onPick: (g: CastGeschlecht) => void;
}

function GenderQuestion({ name, value, onPick }: GenderQuestionProps) {
  const title = STRINGS.book.personen.qGenderTitle.replace(
    '{Name}',
    name.trim().length > 0 ? name.trim() : '',
  );
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-dream-navy">{title}</h3>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => onPick('maennlich')}
          className={BUTTON_CHOICE}
          aria-pressed={value === 'maennlich'}
        >
          {STRINGS.book.personen.qGenderMale}
        </button>
        <button
          type="button"
          onClick={() => onPick('weiblich')}
          className={BUTTON_CHOICE}
          aria-pressed={value === 'weiblich'}
        >
          {STRINGS.book.personen.qGenderFemale}
        </button>
        <button
          type="button"
          onClick={() => onPick('keineAngabe')}
          className={BUTTON_CHOICE}
          aria-pressed={value === 'keineAngabe'}
        >
          {STRINGS.book.personen.qGenderNone}
        </button>
      </div>
    </div>
  );
}

interface RelationQuestionProps {
  readonly role: FlowRole;
  readonly name: string;
  readonly value: string;
  readonly onChange: (text: string) => void;
  readonly onAdvance: () => void;
  readonly onSkip: () => void;
}

function RelationQuestion({
  role,
  name,
  value,
  onChange,
  onAdvance,
  onSkip,
}: RelationQuestionProps) {
  const title = STRINGS.book.personen.qRelationTitle.replace(
    '{Name}',
    name.trim().length > 0 ? name.trim() : '',
  );
  // Sebastian ruling 2026-09-24 — placeholder depends on role.
  const placeholder =
    role === 'kind'
      ? STRINGS.book.personen.qRelationPlaceholderKind
      : role === 'erwachsener'
        ? STRINGS.book.personen.qRelationPlaceholderErwachsener
        : STRINGS.book.personen.qRelationPlaceholderTier;
  const canAdvance = value.trim().length > 0;
  const handleAdvance = () => {
    if (canAdvance) onAdvance();
  };
  return (
    <div className="space-y-2">
      <label className="block text-base font-semibold text-dream-navy">{title}</label>
      <textarea
        className={`${TEXTAREA_CLASS} min-h-[3rem]`}
        rows={2}
        maxLength={CAST_PERSONEN_NOTIZ_MAX_LENGTH}
        placeholder={placeholder}
        value={value}
        aria-label={STRINGS.book.personen.qRelationTitle}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAdvance();
          }
        }}
      />
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={handleAdvance}
          disabled={!canAdvance}
          className={BUTTON_GOLD}
        >
          {STRINGS.book.personen.qCommonWeiter}
        </button>
        <button type="button" onClick={onSkip} className={BUTTON_OUTLINE_SM}>
          {STRINGS.book.personen.qCommonSkip}
        </button>
      </div>
    </div>
  );
}

interface PhotoQuestionProps {
  readonly name: string;
  readonly photos: CastPhotos;
  readonly onFacePicked: (file: File) => void;
  readonly onFaceRemoved: () => void;
}

function PhotoQuestion({ name, photos, onFacePicked, onFaceRemoved }: PhotoQuestionProps) {
  const title = STRINGS.book.personen.qPhotoTitle.replace(
    '{Name}',
    name.trim().length > 0 ? name.trim() : '',
  );
  return (
    <div className="space-y-3">
      <h3 className="text-base font-semibold text-dream-navy">{title}</h3>
      <section
        aria-label={STRINGS.book.personen.fotoPrivacyHeading}
        className="rounded-lg border border-dream-navy/15 bg-dream-white p-3 text-sm text-dream-navy/80"
      >
        <p className="mb-2 font-semibold text-dream-navy">
          {STRINGS.book.personen.fotoPrivacyHeading}
        </p>
        <ul className="space-y-1 text-xs text-dream-navy/80">
          <li>· {STRINGS.book.personen.fotoPrivacyUse}</li>
          <li>· {STRINGS.book.personen.fotoPrivacyDelete}</li>
          <li>· {STRINGS.book.personen.fotoPrivacyStorage}</li>
        </ul>
        <a
          href={STRINGS.book.personen.fotoPrivacyLinkHref}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-block text-xs text-dream-navy underline"
        >
          {STRINGS.book.personen.fotoPrivacyLinkText}
        </a>
      </section>
      <FotoUploadSlot
        state={faceSlotState(photos)}
        onFileSelected={onFacePicked}
        onRemove={onFaceRemoved}
        showRules={false}
        showLimitsLine={false}
        ariaLabel="Foto des Gesichts hochladen"
        castName={name.trim()}
      />
      <p className="text-xs text-dream-navy/70">{STRINGS.book.personen.qPhotoBelow}</p>
      <p className="text-xs text-dream-navy/70">{STRINGS.book.personen.fotoLimitsLine}</p>
      <details>
        <summary className="cursor-pointer text-xs text-dream-navy/70 underline">
          {STRINGS.book.personen.qPhotoTipsToggle}
        </summary>
        <ul className="mt-2 space-y-0.5 text-xs text-dream-navy/70">
          <li>· {STRINGS.book.personen.fotoRules.onePerImage}</li>
          <li>· {STRINGS.book.personen.fotoRules.faceVisible}</li>
          <li>· {STRINGS.book.personen.fotoRules.noSunglassesCap}</li>
          <li>· {STRINGS.book.personen.fotoRules.everydayBetterThanStudio}</li>
        </ul>
      </details>
    </div>
  );
}

// ─── AddAnother prompt ────────────────────────────────────────────

interface AddAnotherPromptProps {
  readonly canAddMore: boolean;
  readonly onAddAnother: (role: FlowRole) => void;
  readonly onFinish: () => void;
}

function AddAnotherPrompt({ canAddMore, onAddAnother, onFinish }: AddAnotherPromptProps) {
  const [showRolePicker, setShowRolePicker] = useState(false);
  if (!canAddMore) {
    return (
      <div className="space-y-3 pt-2">
        <p
          role="status"
          className="rounded-lg border border-dream-navy/15 bg-dream-white p-3 text-sm text-dream-navy/80"
        >
          {STRINGS.book.personen.personenCapReachedNote}
        </p>
        <button type="button" onClick={onFinish} className={BUTTON_GOLD}>
          {STRINGS.book.personen.addAnotherNo}
        </button>
      </div>
    );
  }
  if (showRolePicker) {
    return (
      <div className="space-y-3 pt-2">
        <h3 className="text-base font-semibold text-dream-navy">
          {STRINGS.book.personen.qRoleTitle}
        </h3>
        <div className="flex flex-col gap-2">
          <button type="button" onClick={() => onAddAnother('kind')} className={BUTTON_CHOICE}>
            {STRINGS.book.personen.qRoleKind}
          </button>
          <button
            type="button"
            onClick={() => onAddAnother('erwachsener')}
            className={BUTTON_CHOICE}
          >
            {STRINGS.book.personen.qRoleErwachsener}
          </button>
          <button type="button" onClick={() => onAddAnother('tier')} className={BUTTON_CHOICE}>
            {STRINGS.book.personen.qRoleTier}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3 pt-2">
      <h3 className="text-base font-semibold text-dream-navy">
        {STRINGS.book.personen.addAnotherTitle}
      </h3>
      <div className="flex flex-col gap-2">
        <button type="button" onClick={() => setShowRolePicker(true)} className={BUTTON_OUTLINE_SM}>
          {STRINGS.book.personen.addAnotherYes}
        </button>
        <button type="button" onClick={onFinish} className={BUTTON_GOLD}>
          {STRINGS.book.personen.addAnotherNo}
        </button>
      </div>
    </div>
  );
}

// ─── Photo slot helpers ────────────────────────────────────────────

function faceSlotState(photos: CastPhotos): FotoUploadSlotState {
  return entryToSlotState(photos.face);
}

function entryToSlotState(entry: PhotoEntry | null): FotoUploadSlotState {
  if (entry === null) return { kind: 'empty' };
  if (entry.status === 'heic') return { kind: 'heic' };
  if (entry.status === 'converting') return { kind: 'converting' };
  if (entry.url !== null) return { kind: 'picked', previewUrl: entry.url };
  return { kind: 'heic' };
}

// Re-export type for tests.
export type { CastPersonType };
